export enum AgentMode {
  DEFAULT_MODE,
  AUTO_ACCEPT_EDITS_MODE,
  AUTO_MODE,
  PLAN_MODE,
  BYPASS_PERMISSIONS_MODE,
  DONT_ASK_MODE,
  UNKNOWN,
}

export interface AgentModeColors {
  readonly bg: string
  readonly text: string
  readonly border: string
}

export function agentModeFrom(value: string): AgentMode {
  const normalized = value.toLowerCase().replaceAll(/[-_]/g, '')

  switch (normalized) {
    case 'defaultmode':
    case 'default':
      return AgentMode.DEFAULT_MODE
    case 'autoaccepteditsmode':
    case 'autoacceptedits':
      return AgentMode.AUTO_ACCEPT_EDITS_MODE
    case 'automode':
    case 'auto':
      return AgentMode.AUTO_MODE
    case 'planmode':
    case 'plan':
      return AgentMode.PLAN_MODE
    case 'bypasspermissionsmode':
    case 'bypasspermissions':
      return AgentMode.BYPASS_PERMISSIONS_MODE
    case 'dontaskmode':
    case 'dontask':
      return AgentMode.DONT_ASK_MODE
    case 'unknown':
      return AgentMode.UNKNOWN
    default:
      return AgentMode.UNKNOWN
  }
}

export function agentModeToString(mode: AgentMode): string {
  switch (mode) {
    case AgentMode.DEFAULT_MODE:
      return 'DEFAULT_MODE'
    case AgentMode.PLAN_MODE:
      return 'PLAN_MODE'
    case AgentMode.AUTO_ACCEPT_EDITS_MODE:
      return 'AUTO_ACCEPT_EDITS_MODE'
    case AgentMode.AUTO_MODE:
      return 'AUTO_MODE'
    case AgentMode.BYPASS_PERMISSIONS_MODE:
      return 'BYPASS_PERMISSIONS_MODE'
    case AgentMode.DONT_ASK_MODE:
      return 'DONT_ASK_MODE'
    case AgentMode.UNKNOWN:
      return 'UNKNOWN'
    default:
      return `UNKNOWN(${mode})`
  }
}

export function resolveAgentModeBadgeColor(
  mode: AgentMode,
  paletteMode: 'light' | 'dark' = 'dark',
): AgentModeColors {
  if (paletteMode === 'light') {
    switch (mode) {
      case AgentMode.DEFAULT_MODE:
        return { bg: '#D7CCC8', text: '#4E342E', border: '#BCAAA4' }
      case AgentMode.PLAN_MODE:
        return { bg: '#B2DFDB', text: '#004D40', border: '#80CBC4' }
      case AgentMode.AUTO_ACCEPT_EDITS_MODE:
        return { bg: '#E1BEE7', text: '#4A148C', border: '#CE93D8' }
      case AgentMode.AUTO_MODE:
        return { bg: '#FFF59D', text: '#F57F17', border: '#FFE082' }
      case AgentMode.BYPASS_PERMISSIONS_MODE:
        return { bg: '#FFCDD2', text: '#B71C1C', border: '#EF9A9A' }
      case AgentMode.DONT_ASK_MODE:
      case AgentMode.UNKNOWN:
        return { bg: '#FFF8E1', text: '#7B5B3A', border: '#FFE082' }
    }
  }

  switch (mode) {
    case AgentMode.DEFAULT_MODE:
      return { bg: '#f5f5f5', text: '#2e2e2e', border: '#e0e0e0' }
    case AgentMode.PLAN_MODE:
      return { bg: '#00acc1', text: '#ffffff', border: '#00acc1' }
    case AgentMode.AUTO_ACCEPT_EDITS_MODE:
      return { bg: '#ba68c8', text: '#ffffff', border: '#ba68c8' }
    case AgentMode.AUTO_MODE:
      return { bg: '#fbc02d', text: '#1a1a1a', border: '#f9a825' }
    case AgentMode.BYPASS_PERMISSIONS_MODE:
      return { bg: '#c62828', text: '#ffffff', border: '#c62828' }
    case AgentMode.DONT_ASK_MODE:
    case AgentMode.UNKNOWN:
      return { bg: '#fef3c7', text: '#92400e', border: '#fde68a' }
  }
}

export function resolveAgentModeBadgeLabel(mode: AgentMode): string {
  switch (mode) {
    case AgentMode.DEFAULT_MODE:
      return 'Default'
    case AgentMode.PLAN_MODE:
      return 'Plan'
    case AgentMode.AUTO_ACCEPT_EDITS_MODE:
      return 'Accept edits'
    case AgentMode.AUTO_MODE:
      return 'Auto'
    case AgentMode.BYPASS_PERMISSIONS_MODE:
      return 'Bypass permissions'
    case AgentMode.DONT_ASK_MODE:
      return "Don't ask"
    case AgentMode.UNKNOWN:
      return 'Unknown'
  }
}

export function isUserSelectableMode(
  mode: AgentMode,
  dangerouslySkipPermissions?: boolean,
  autoModeSupported?: boolean,
): boolean {
  const baseSelectableModes =
    mode === AgentMode.DEFAULT_MODE ||
    mode === AgentMode.AUTO_ACCEPT_EDITS_MODE ||
    mode === AgentMode.PLAN_MODE

  if (mode === AgentMode.BYPASS_PERMISSIONS_MODE) {
    return dangerouslySkipPermissions === true
  }

  if (mode === AgentMode.AUTO_MODE) {
    return autoModeSupported === true
  }

  if (mode === AgentMode.DONT_ASK_MODE) {
    return false
  }

  return baseSelectableModes
}

export function canTransitionFrom(mode: AgentMode): boolean {
  return mode !== AgentMode.UNKNOWN
}
